import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { useLanguage } from "../../i18n/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Loader2 } from "lucide-react";

const TIERS = ["user", "partner"];

const UsersEntitlementsTable = ({ users, onUpdated }) => {
  const { t } = useLanguage();
  const [savingId, setSavingId] = useState(null);
  const [draftTiers, setDraftTiers] = useState({});

  const getDraftTier = (row) => draftTiers[row.user_id] ?? row.access_tier ?? "user";

  const handleTierChange = async (row, tier) => {
    setDraftTiers((prev) => ({ ...prev, [row.user_id]: tier }));
    setSavingId(row.user_id);
    try {
      await axios.post(`${API}/platform/users/${row.user_id}/entitlement`, { tier });
      toast.success(t("platform.tierSaved"));
      onUpdated?.();
    } catch (err) {
      setDraftTiers((prev) => {
        const next = { ...prev };
        delete next[row.user_id];
        return next;
      });
      toast.error(err.response?.data?.detail || t("platform.tierError"));
    } finally {
      setSavingId(null);
    }
  };

  if (!users || users.length === 0) {
    return (
      <Card className="border border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">{t("platform.usersTitle")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">{t("platform.noData")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-border" data-testid="platform-users-entitlements">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium">{t("platform.usersTitle")}</CardTitle>
        <CardDescription>{t("platform.usersDesc")}</CardDescription>
      </CardHeader>
      <CardContent className="px-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-muted-foreground text-xs">
                <th className="text-left px-4 pb-2 font-medium">{t("platform.user")}</th>
                <th className="text-left px-4 pb-2 font-medium hidden md:table-cell">
                  {t("platform.registered")}
                </th>
                <th className="text-right px-4 pb-2 font-medium">{t("platform.queries")}</th>
                <th className="text-right px-4 pb-2 font-medium">{t("platform.spend")}</th>
                <th className="text-left px-4 pb-2 font-medium w-36">{t("platform.accessTier")}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const tier = getDraftTier(u);
                const saving = savingId === u.user_id;
                return (
                  <tr key={u.user_id} className="border-b border-border/50 hover:bg-accent/50">
                    <td className="px-4 py-2.5">
                      <div className="font-medium">{u.name || u.email}</div>
                      {u.name && <div className="text-xs text-muted-foreground">{u.email}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground text-xs hidden md:table-cell">
                      {u.registered_at ? u.registered_at.slice(0, 10) : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right">{u.stats?.total_consultations ?? 0}</td>
                    <td className="px-4 py-2.5 text-right font-medium">
                      ${(u.stats?.total_cost_usd ?? 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-2.5">
                      <Select
                        value={tier}
                        disabled={saving || u.role === "superadmin"}
                        onValueChange={(value) => handleTierChange(u, value)}
                      >
                        <SelectTrigger className="h-8 text-xs" aria-label={t("platform.accessTier")}>
                          {saving ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <SelectValue />
                          )}
                        </SelectTrigger>
                        <SelectContent>
                          {TIERS.map((key) => (
                            <SelectItem key={key} value={key}>
                              {t(`platform.tier.${key}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default UsersEntitlementsTable;
